import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import { fetchProductById } from '../api/products';
import { useCheckout } from '../context/CheckoutContext';
import { formatIdrFromUsd } from '../utils/currency';

const CheckoutConfirmation = () => {
  const navigate = useNavigate();
  const { checkoutData, hasRequiredDataForStep } = useCheckout();
  const { productId, selectedSize, productName } = checkoutData;
  // We use product.name from the API, but can fall back to productName if needed
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Check if we have the required data for this step
  useEffect(() => {
    if (!hasRequiredDataForStep('confirmation')) {
      navigate('/products');
    }
  }, [hasRequiredDataForStep, navigate]);

  // Fetch product data if we have a productId
  useEffect(() => {
    const getProduct = async () => {
      if (!productId) {
        setError('No product selected');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const data = await fetchProductById(productId);
        
        if (!data) {
          setError('Product not found');
        } else {
          setProduct(data);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError('Failed to load product. Please try again later.');
        setLoading(false);
      }
    };
    
    getProduct();
  }, [productId]);
  
  // Handle continue to shipping
  const handleContinueToShipping = () => {
    navigate('/checkout/shipping');
  };
  
  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-light">Loading order details...</p>
        </div>
      </Layout>
    );
  }
  
  // Error state
  if (error || !product) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="font-serif text-3xl font-bold text-primary mb-6">Order Error</h1>
          <p className="text-light mb-8">{error || 'An error occurred while loading your order.'}</p>
          {productName && (
            <p className="text-light mb-8">
              We couldn't load the details for "{productName}", but you can still proceed with your order.
            </p>
          )}
          <div className="flex justify-center space-x-4">
            <Button onClick={() => navigate('/products')} variant="secondary">
              Back to Products
            </Button>
            {productName && (
              <Button onClick={handleContinueToShipping} variant="primary">
                Continue Anyway
              </Button>
            )}
          </div>
        </div>
      </Layout>
    );
  }
  
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-3xl mx-auto">
          {/* Checkout Steps */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-primary text-dark rounded-full flex items-center justify-center font-bold">1</div>
                <span className="text-primary text-sm mt-2">Confirmation</span>
              </div>
              <div className="flex-1 h-1 bg-gray-700 mx-2"></div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-gray-700 text-gray-400 rounded-full flex items-center justify-center font-bold">2</div>
                <span className="text-gray-400 text-sm mt-2">Shipping</span>
              </div>
              <div className="flex-1 h-1 bg-gray-700 mx-2"></div>
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-gray-700 text-gray-400 rounded-full flex items-center justify-center font-bold">3</div>
                <span className="text-gray-400 text-sm mt-2">Payment</span>
              </div>
            </div>
          </div>
          
          <h1 className="font-serif text-3xl font-bold text-primary mb-6">
            Order Confirmation
          </h1>
          
          <div className="bg-secondary border border-gray-800 rounded-lg overflow-hidden mb-8">
            <div className="p-6">
              <h2 className="font-serif text-xl font-bold text-primary mb-4">
                Order Summary
              </h2>
              
              <div className="flex items-start mb-6">
                {product.images && product.images.length > 0 ? (
                  <img 
                    src={product.images[0]} 
                    alt={product.name} 
                    className="w-24 h-24 object-cover object-center mr-4 border border-gray-800"
                    onError={(e) => {
                      e.target.src = 'https://placehold.co/100x100/333/666?text=Image+Not+Found';
                    }}
                  />
                ) : (
                  <div className="w-24 h-24 bg-gray-800 flex items-center justify-center mr-4 border border-gray-800">
                    <p className="text-gray-400 text-xs">No image</p>
                  </div>
                )}
                
                <div className="flex-1">
                  <h3 className="font-serif text-lg font-bold text-light mb-1">
                    {product.name}
                  </h3>
                  <p className="text-gray-400 text-sm mb-2">
                    {selectedSize ? `Size: ${selectedSize}` : 'Custom Size'}
                  </p>
                  <p className="text-gray-400 text-sm">
                    {product.material && `Material: ${product.material}`}
                    {product.color && `, Color: ${product.color}`}
                  </p>
                </div>
                
                <div className="text-right">
                  <span className="text-xl font-bold text-primary">
                    {formatIdrFromUsd(product.price)}
                  </span>
                </div>
              </div>
              
              <div className="border-t border-gray-800 pt-4">
                <div className="flex justify-between mb-2">
                  <span className="text-gray-400">Subtotal</span>
                  <span className="text-light">{formatIdrFromUsd(product.price)}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-gray-400">Shipping</span>
                  <span className="text-light">Calculated at next step</span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-800 mt-2">
                  <span className="text-light">Total</span>
                  <span className="text-primary">{formatIdrFromUsd(product.price)} + Shipping</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-secondary border border-gray-800 rounded-lg overflow-hidden mb-8">
            <div className="p-6">
              <h2 className="font-serif text-xl font-bold text-primary mb-4">
                Important Information
              </h2>
              <p className="text-light mb-4">
                Each Kala hat is handcrafted in Bali with meticulous attention to detail. 
                Your hat will be made to order based on your specifications.
              </p>
              <p className="text-light mb-4">
                In the next steps, you'll provide your shipping address and select a shipping method.
                The shipping cost will be calculated based on your location and the shipping method you choose.
              </p>
              <p className="text-light">
                The shipping box dimensions are 40x40x17 cm.
              </p>
            </div>
          </div>
          
          <div className="flex justify-between">
            <Button 
              variant="secondary" 
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            <Button 
              variant="primary" 
              onClick={handleContinueToShipping}
            >
              Continue to Shipping
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CheckoutConfirmation;
